/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState, useEffect } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";

interface ScheduleAudioPlayer{
    src: string;
    currentPlaying?: boolean; 
    setPlaying?: any
}

export function CustomAudioPlayer({ src }: { src: string }) {

  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      await audioRef.current.play();
      setIsPlaying(true);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">

      {/* Custom Play Button */}
      <button
        onClick={togglePlay}
        className="bg-[#55afba] px-4 py-2 rounded text-white"
      >
        {isPlaying ? "Pause" : "Play"}
      </button>

      {/* Hidden audio engine */}
      <audio ref={audioRef} src={src} className="hidden" />

    </div>
  );
}

export function ScheduleAudioPlayer({ src, currentPlaying, setPlaying }: ScheduleAudioPlayer) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying){
        audioRef.current.pause();
        setPlaying(null);
    } 
    else{
        audioRef.current.play();
        setPlaying("normal_song");
    } 
    setIsPlaying(!isPlaying);
  };

  const skipTime = (seconds: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(
      Math.max(audioRef.current.currentTime + seconds, 0),
      duration
    );
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
    };
  }, [src]);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Number(e.target.value);
    setCurrentTime(Number(e.target.value));
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const vol = Number(e.target.value);
    audioRef.current.volume = vol;
    setVolume(vol);
  };

  const formatTime = (time: number) => {
    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  useEffect(() => {
    if (!currentPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false)
    }
  }, [currentPlaying]);

  return (
    <div className="w-full p-4 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-none space-y-3 relative">
      {/* Hidden audio */}
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Seek bar */}
      <div className="w-full">
        <input
          type="range"
          min={0}
          max={duration || 0}
          value={currentTime}
          onChange={handleSeek}
          className="w-full h-1 rounded-full bg-white/20 accent-[#55afba]"
        />
        <div className="flex justify-between mt-1 text-xs text-white/70">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mt-2">
        {/* Left buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => skipTime(-10)}
            className="bg-[#174868] hover:bg-[#55afba]/40 p-3 rounded-full text-white transition-colors"
          >
            <SkipBack className="w-5 h-5" />
          </button>

          <button
            onClick={togglePlay}
            className="bg-[#55afba] hover:bg-[#55afba]/70 p-4 rounded-full text-black transition-colors"
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          </button>

          <button
            onClick={() => skipTime(10)}
            className="bg-[#174868] hover:bg-[#55afba]/40 p-3 rounded-full text-white transition-colors"
          >
            <SkipForward className="w-5 h-5" />
          </button>
        </div>

        {/* Right volume */}
        <div className="flex items-center gap-2 w-28">
          {volume > 0 ? <Volume2 className="w-4 h-4 text-white/80" /> : <VolumeX className="w-4 h-4 text-white/80" />}
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={handleVolumeChange}
            className="h-1 rounded-full bg-white/20 accent-[#55afba] w-full"
          />
        </div>
      </div>
    </div>
  );
}
